import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import Crop from '@src/components/Crop';
import { IconCheck, IconClose } from '@src/components/Icon';

import useStyles from 'isomorphic-style-loader/useStyles';
import css from './style.module.sass';

const VenueFeatures = (props) => {
	useStyles(css);
	const {
		checkItems,
		textItems,
		specText,
		isWithEmptyBlocks,
		checkItemGroups,
		StatusPositiveIcon,
		StatusNegativeIcon,
	} = props;

	const isTopBlockPresent = !!specText;
	const isBottomBlockPresent =
		textItems.length > 0 ||
		checkItems.length > 0 ||
		checkItemGroups.length > 0;

	const renderItems = React.useCallback(
		(item) => {
			const nodeClass = cn([
				'grk-ui-status-node',
				{ '-nonactive': !item.checked },
			]);
			return (
				<div key={item.id} className={nodeClass}>
					{item.checked
						? StatusPositiveIcon && (
								<StatusPositiveIcon className={css.iconCheck} />
						  )
						: StatusNegativeIcon && (
								<StatusNegativeIcon className={css.iconClose} />
						  )}
					<span className="">{item.text}</span>
				</div>
			);
		},
		[StatusPositiveIcon, StatusNegativeIcon],
	);

	return (
		<div className={cn('prfl-node-border', css.wrap)}>
			{specText && (
				<Crop value={t('Развернуть')} maxHeight={200}>
					{specText}
				</Crop>
			)}
			{!specText && isWithEmptyBlocks && (
				<>
					<div className="prfl-empty-title">
						{t('Описание заведения не добавлено')}
					</div>
					<div className="prfl-empty-desc">
						<div className="prfl-empty-line" />
						<div className="prfl-empty-line" />
					</div>
				</>
			)}
			{((isTopBlockPresent && isBottomBlockPresent) ||
				isWithEmptyBlocks) && <div className={css.separator} />}
			{(textItems.length > 0 ||
				checkItems.length > 0 ||
				checkItemGroups.length > 0) && (
				<div className="prfl-feature">
					{textItems.length > 0 && (
						<Crop value={t('Развернуть')} maxHeight={200}>
							{textItems.reduce((result, item) => {
								return `${result}<p><b>${item.name}:</b> ${item.text}</p>`;
							}, '')}
						</Crop>
					)}
					{checkItems.length > 0 && (
						<div className="grk-ui-status">
							{checkItems.map(renderItems)}
						</div>
					)}
					{checkItemGroups.length > 0 && (
						<div className="__groups">
							{checkItemGroups.map((group) => {
								return (
									<div
										key={group.groupName}
										className="__group"
									>
										<div className="__group-title">
											{group.groupName}
										</div>
										{group.list?.length > 0 && (
											<div>
												{group.list.map(renderItems)}
											</div>
										)}
									</div>
								);
							})}
						</div>
					)}
				</div>
			)}
			{isWithEmptyBlocks &&
				textItems.length === 0 &&
				checkItems.length === 0 && (
					<>
						<div className="prfl-empty-title">
							{t('Особенности не указаны')}
						</div>
						<div className="prfl-empty-desc">
							<div className="prfl-empty-line" />
							<div className="prfl-empty-line" />
						</div>
					</>
				)}
		</div>
	);
};

VenueFeatures.propTypes = {
	/**
	 * Текст описания заведения. Используется обрезка текста, можно применять _HTML_.
	 */
	specText: PropTypes.string,
	/**
	 * Контроллер наличия/отсутствия памятки об отсутствующем описании заведения.
	 */
	isWithEmptyBlocks: PropTypes.bool,
	/**
	 * Массив объектов, описывающих присутствующие/отсутствующие особенности заведения.<br>
	 * Где:<br>
	 * <ul>
	 *   <li><b>id</b> — идентификатор особенности;</li>
	 *   <li><b>text</b> — краткое описание особенности;</li>
	 *   <li><b>checked</b> — контроллер наличия/отсутствия особенности.</li>
	 * </ul>
	 */
	checkItems: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number,
			text: PropTypes.string,
			checked: PropTypes.bool,
		}),
	),
	checkItemGroups: PropTypes.arrayOf(
		PropTypes.shape({
			groupName: PropTypes.string,
			list: PropTypes.arrayOf(
				PropTypes.shape({
					id: PropTypes.number,
					text: PropTypes.string,
					checked: PropTypes.bool,
				}),
			),
		}),
	),
	/**
	 * Массив объектов, содержащих текстовое описание особенностей заведения.<br>
	 * Где:<br>
	 * <ul>
	 *   <li><b>id</b> — идентификатор особенности;</li>
	 *   <li><b>name</b> — название особенности;</li>
	 *   <li><b>text</b> — подробное описание особенности.</li>
	 * </ul>
	 */
	textItems: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
			name: PropTypes.string,
			text: PropTypes.string,
		}),
	),
	/**
	 * React-компонент, иконка наличия особенности.
	 */
	StatusPositiveIcon: PropTypes.elementType,
	/**
	 * React-компонент, иконка отсутствия особенности.
	 */
	StatusNegativeIcon: PropTypes.elementType,
};

VenueFeatures.defaultProps = {
	checkItems: [],
	textItems: [],
	checkItemGroups: [],
	specText: undefined,
	isWithEmptyBlocks: false,
	StatusPositiveIcon: (props) => <IconCheck {...props} />,
	StatusNegativeIcon: (props) => <IconClose {...props} />,
};

export default VenueFeatures;
